import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IArtistDetailResponse,
  IArtistFilteredListParams,
  IArtistPagesResponse,
  IArtistResponse,
  ICreateArtist,
  IUpdateArtist,
} from './types';

export const useGetArtistListInfinite = (params: IArtistFilteredListParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IArtistResponse[], DefaultError>({
    queryKey: ['artists', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Artist/Search',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<IArtistPagesResponse>(response, {
        groupName: `useGetArtistListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IArtistResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetArtist = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IArtistDetailResponse, DefaultError>({
    queryKey: ['artist', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Artist/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetArtist',
        notFoundPath: '/artist',
      });
    },
  });
};

export const usePostArtist = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePortArtist');
  return useMutation<IArtistResponse, DefaultError, ICreateArtist>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('artist.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'Artist',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostArtist',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.nickname ?? i18next.t('artist.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateArtist = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useUpdateArtist');
  return useMutation<IArtistResponse, DefaultError, IUpdateArtist>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('artist.what'),
        })
      );
      const response = await apiClient({ method: 'PUT', url: 'Artist', body });

      return responseHandler(response, {
        groupName: 'useUpdateArtist',
        notFoundPath: '/artist',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.nickname ?? i18next.t('artist.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteArtist = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteArtist');
  return useMutation<{}, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('artist.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `Artist/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteArtist',
        notFoundPath: '/artist',
      });
    },

    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('artist.what'),
        })
      );
    },
    onError: dismiss,
  });
};
